import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import * as Yup from 'yup';
import i18n from 'i18n-js';
import Grid from '@material-ui/core/Grid';
import { BaseModal } from 'components/UI';
import { Formik, Form, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { TextField, MaterialSelectField } from 'components/FormFields';
import { states } from 'constants';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import useStyle from './EditAddress.styled';
import { SubscriptionsActions } from 'state/modules/subscriptions';
import { cloneDeep } from 'lodash';

const EditAddress = ({ open, subscription, addressType, handleClick }) => {
  const formSchema = Yup.object().shape({
    street_address_line_1: Yup.string().required(i18n.t('common.required_field')),
    street_address_line_2: Yup.string(),
    city: Yup.string().required(i18n.t('common.required_field')),
    state: Yup.string().required(i18n.t('common.required_field')),
    zip_code: Yup.string().required(i18n.t('common.required_field')),
  });

  const classes = useStyle();
  const formRef = useRef(null);
  const dispatch = useDispatch();

  const { fetchedUpdateExchange, fetchingUpdateExchange } = useSelector(
    (state) => state.subscriptions
  );

  const [currentType, setCurrentType] = useState(null);
  const [exchangeType, setExchangeType] = useState(null);
  const [onlyText, setOnlyText] = useState(true);

  const [initialValues, setInitialValues] = useState({
    street_address_line_1: '',
    street_address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
  });

  const handleAddressType = (event, type) => {
    if (type) {
      if (event) {
        setExchangeType(currentType);
        setCurrentType(type);
        dispatch(
          SubscriptionsActions.fetchUpdateExchange(subscription?.id, {
            exchange_event: subscription?.status === 'INPROGRESS' ? 'END' : 'START',
            action: 'SETEXCHANGETYPE',
            exchange_type: type,
          })
        );
      } else {
        setCurrentType(type);
      }
    }
  };

  const onSubmit = () => {
    formRef.current.submitForm();
  };

  const setAddress = (data) => {
    setInitialValues({
      street_address_line_1: data?.street_address_line_1 || '',
      street_address_line_2: data?.street_address_line_2 || '',
      city: data?.city || '',
      state: data?.state || '',
      zip_code: data?.zip_code || '',
    });
  };

  useEffect(() => {
    if (subscription) {
      // valid statuses for edit address
      const endData = {
        statuses: ['INPROGRESS', 'ENDEXCHANGE'],
        enable: ['INPROGRESS'],
      };
      const startData = {
        statuses: ['PENDING', 'STARTEXCHANGE'],
      };

      if (
        endData.statuses.includes(subscription.status) &&
        endData?.enable?.includes(subscription.status)
      ) {
        setOnlyText(false);
      }

      if (startData.statuses.includes(subscription.status)) {
        setOnlyText(false);
      }
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!fetchingUpdateExchange && fetchedUpdateExchange && exchangeType) {
      setExchangeType(null);
    }
    if (!fetchingUpdateExchange && !fetchedUpdateExchange && exchangeType) {
      setCurrentType(exchangeType);
      setExchangeType(null);
    }
    if (fetchedUpdateExchange && addressType === 'address') {
      handleClick();
    }
  }, [fetchingUpdateExchange, fetchedUpdateExchange ]); // eslint-disable-line

  useEffect(() => {
    if (subscription) {
      const exchanges = subscription?.exchanges || {};

      if (['PENDING', 'STARTEXCHANGE'].includes(subscription.status)) {
        handleAddressType(null, exchanges?.START?.exchange_type || 'VEHICLELOCATION');
        setAddress(exchanges?.START?.exchange_address || {});
      } else {
        handleAddressType(null, exchanges?.END?.exchange_type || 'VEHICLELOCATION');
        setAddress(exchanges?.END?.exchange_address || {});
      }
    }
  }, [subscription]); // eslint-disable-line

  const setField = (type, name, label, values, data) => {
    if (!onlyText && addressType !== 'vehicle') {
      switch (type) {
        case 'select':
          return (
            <Field
              name={name}
              options={data.option}
              readOnly={data.readOnly}
              component={data.component}
              placeholder={label}
            />
          );
        default:
          return (
            <Field type="text" name={name} margin="none" component={TextField} label={label} />
          );
      }
    } else {
      return (
        <div>
          <h5> {label} </h5>
          <p> {values[name] || '--'} </p>
        </div>
      );
    }
  };

  const onEditAddress = (values) => {
    if (addressType === 'vehicle') {
      return handleClick();
    }
    const data = cloneDeep(values);
    dispatch(
      SubscriptionsActions.fetchUpdateExchange(subscription?.id, {
        exchange_event: subscription?.status === 'INPROGRESS' ? 'END' : 'START',
        action: 'SETEXCHANGETYPE',
        exchange_type: currentType,
        address: data,
      })
    );
  };

  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton={addressType === 'vehicle' || onlyText ? false : true}
      onEnter={onlyText ? handleClick : onSubmit}
      onCancel={handleClick}
      enableCloseButton={true}
      handleClick={handleClick}
      containerCustomClass={classes?.modalHeader}
      enterButtonLabel={
        onlyText || addressType === 'vehicle' ? i18n.t('common.close') : i18n.t('common.save')
      }
      loading={fetchingUpdateExchange}
      title={
        onlyText
          ? i18n.t('common.address')
          : addressType === 'vehicle'
          ? i18n.t('subscriptions.choose_address')
          : i18n.t('subscriptions.edit_user_address')
      }
    >
      {addressType === 'vehicle' && (
        <ToggleButtonGroup
          value={currentType}
          exclusive
          classes={{
            root: classes.toggleButtonGroupRoot,
          }}
          onChange={handleAddressType}
          aria-label="address"
        >
          <ToggleButton
            disabled={fetchingUpdateExchange || onlyText}
            value="VEHICLELOCATION"
            classes={{
              root: classes.toggleButtonRoot,
              selected: classes.toggleButtonSelected,
            }}
            aria-label="pickup"
          >
            {i18n.t('subscriptions.pick_up')}
          </ToggleButton>
          <ToggleButton
            disabled={fetchingUpdateExchange || onlyText}
            value="USERHOME"
            classes={{
              root: classes.toggleButtonRoot,
              selected: classes.toggleButtonSelected,
            }}
            aria-label="delivery"
          >
            {i18n.t('subscriptions.delivery')}
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      {!onlyText && addressType === 'vehicle' && (
        <p style={{ marginBottom: 15 }}>{i18n.t('subscriptions.address_description')}</p>
      )}
      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onEditAddress}
        validationSchema={formSchema}
      >
        {({ handleSubmit, values, errors }) => (
          <Form noValidate onSubmit={handleSubmit} autoComplete="off">
            <Grid data-cy="edit-address-modal" container spacing={2}>
              <Grid item xs={12}>
                {setField('text', 'street_address_line_1', `${i18n.t('common.address')} 1`, values)}
              </Grid>
              <Grid item xs={12}>
                {setField('text', 'street_address_line_2', `${i18n.t('common.address')} 2`, values)}
              </Grid>
              <Grid item xs={12}>
                {setField('text', 'city', i18n.t('common.city'), values)}
              </Grid>
              <Grid item xs={12}>
                {setField('text', 'state', i18n.t('common.state'), values, {
                  options: states?.us,
                  readOnly: states?.us?.length === 0,
                  component: MaterialSelectField,
                  label: i18n.t('common.state'),
                })}
              </Grid>
              <Grid item xs={12}>
                {setField('text', 'zip_code', i18n.t('location.zip_code'), values)}
              </Grid>
              {!onlyText && addressType === 'vehicle' && (
                <Grid item xs={12}>
                  <label> {i18n.t('subscriptions.agree_address')} </label>
                </Grid>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

EditAddress.propTypes = {
  current: PropTypes.object,
  open: PropTypes.bool.isRequired,
  addressType: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

EditAddress.defaultProps = {
  open: false,
  addressType: 'vehicle',
  handleClick: () => {},
};

export default EditAddress;
